import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Building2, Calendar, CheckCircle2, Mail, User } from 'lucide-react';
import { useState } from 'react';
import { AppLayout } from '../components/layout/app-layout';
import { PageHeader } from '../components/layout/page-header';
import { AppModal } from '../components/ui/app-modal';
import { Button } from '../components/ui/button';
import { DataTable, type DataTableColumn } from '../components/ui/data-table';
import { FieldBox } from '../components/ui/field-box';
import { usePermissions } from '../hooks/use-permissions';
import { api } from '../lib/ky';
import { cn } from '../lib/utils';
import { useAuthStore } from '../stores/auth.store';

// ── Types ─────────────────────────────────────────────────────────────────────

type ProcessingStatus = 'PENDING' | 'FAILED' | 'SUCCESS';

interface InboundLog {
  id: string;
  referenceNo: string;
  processingStatus: ProcessingStatus;
  errorMessage: string | null;
  receivedAt: string;
  processedAt: string | null;
  requestId: string | null;
  manuallyCreatedAt: string | null;
  manuallyCreatedBy: { id: string; name: string } | null;
  account: { id: string; name: string } | null;
  contact: { id: string; fullName: string; email: string } | null;
  request: { id: string; referenceNo: string } | null;
}

interface InboundLogDetail extends InboundLog {
  payload: unknown;
  request: { id: string; referenceNo: string; status: string } | null;
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/tahseel-inbound')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('sm-study:tahseel-inbound:view'))
      throw redirect({ to: '/dashboard' });
  },
  component: TahseelInboundPage,
});

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_CFG: Record<ProcessingStatus, { label: string; className: string }> = {
  PENDING: { label: 'Pending', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  FAILED: { label: 'Failed', className: 'bg-error-50 text-error-600 border-error-200' },
  SUCCESS: { label: 'Success', className: 'bg-success-50 text-success-600 border-success-200' },
};

function StatusBadge({ status }: { status: ProcessingStatus }) {
  const cfg = STATUS_CFG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

function fmt(iso: string | null) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

function LogDetailModal({
  logId,
  onClose,
  canCreate,
  onCreated,
}: {
  logId: string;
  onClose: () => void;
  canCreate: boolean;
  onCreated: () => void;
}) {
  const navigate = useNavigate();

  const { data: log, isLoading } = useQuery({
    queryKey: ['tahseel-inbound-log', logId],
    queryFn: () => api.get(`study-requests/inbound/logs/${logId}`).json<InboundLogDetail>(),
  });

  const createMutation = useMutation({
    mutationFn: () => api.post(`study-requests/inbound/logs/${logId}/create-request`).json(),
    onSuccess: () => onCreated(),
  });

  const errorMessage =
    createMutation.isError && createMutation.error instanceof Error
      ? createMutation.error.message
      : createMutation.isError
        ? 'Failed to create request. Please try again.'
        : null;

  return (
    <AppModal
      open
      onClose={onClose}
      title="Tahseel Inbound Log"
      description={log?.referenceNo}
      maxWidth="max-w-2xl"
    >
      {isLoading || !log ? (
        <div className="py-8 text-center text-sm text-gray-400">Loading…</div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-mono text-sm font-medium text-gray-900">{log.referenceNo}</span>
            <StatusBadge status={log.processingStatus} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FieldBox icon={<Calendar size={16} />} label="Received">
              {fmt(log.receivedAt)}
            </FieldBox>
            <FieldBox icon={<Calendar size={16} />} label="Processed">
              {fmt(log.processedAt)}
            </FieldBox>
            <FieldBox icon={<Building2 size={16} />} label="Matched Account">
              {log.account ? (
                <Link
                  to="/customers/$customerId"
                  params={{ customerId: log.account.id }}
                  className="font-medium text-brand-600 hover:underline"
                >
                  {log.account.name}
                </Link>
              ) : (
                '—'
              )}
            </FieldBox>
            <FieldBox icon={<User size={16} />} label="Matched Contact">
              {log.contact?.fullName ?? '—'}
            </FieldBox>
          </div>

          {log.request && (
            <div className="rounded-xl border border-success-200 bg-success-50 px-4 py-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-success-600">
                Study Request Created
              </p>
              <button
                type="button"
                onClick={() => {
                  const requestId = log.request?.id;
                  if (requestId)
                    navigate({ to: '/study-requests/$requestId', params: { requestId } });
                }}
                className="text-sm font-medium text-brand-600 hover:underline"
              >
                {log.request.referenceNo}
              </button>
              {log.contact?.email && (
                <p className="mt-1 flex items-center gap-1.5 text-xs text-success-700">
                  <Mail size={11} />
                  {log.contact.email}
                </p>
              )}
            </div>
          )}

          {log.manuallyCreatedAt && (
            <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-500">
              Manually created by {log.manuallyCreatedBy?.name ?? 'an admin'} on{' '}
              {fmt(log.manuallyCreatedAt)}
            </div>
          )}

          {log.errorMessage && (
            <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-error-500">
                Error
              </p>
              <p className="text-sm text-error-700 leading-relaxed">{log.errorMessage}</p>
            </div>
          )}

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Raw Payload
            </p>
            <pre className="max-h-72 overflow-auto rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
              {JSON.stringify(log.payload, null, 2)}
            </pre>
          </div>

          {errorMessage && <p className="text-sm text-error-600">{errorMessage}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
            {canCreate && !log.requestId && (
              <Button
                size="sm"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending}
              >
                <CheckCircle2 size={13} className="mr-1" />
                {createMutation.isPending ? 'Creating…' : 'Create Request'}
              </Button>
            )}
          </div>
        </div>
      )}
    </AppModal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function TahseelInboundPage() {
  const { can } = usePermissions();
  const canCreate = can('sm-study:tahseel-inbound:create-request');
  const qc = useQueryClient();
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const logsKey = ['tahseel-inbound-logs'];

  const {
    data: logs = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: logsKey,
    queryFn: () => api.get('study-requests/inbound/logs').json<InboundLog[]>(),
  });

  const handleCreated = () => {
    setSelectedLogId(null);
    qc.invalidateQueries({ queryKey: logsKey });
  };

  const columns: DataTableColumn<InboundLog>[] = [
    {
      key: 'referenceNo',
      header: 'Reference',
      render: (l) => (
        <button
          type="button"
          onClick={() => setSelectedLogId(l.id)}
          className="font-mono text-sm font-medium text-gray-900 hover:text-brand-600 hover:underline"
        >
          {l.referenceNo}
        </button>
      ),
    },
    {
      key: 'account',
      header: 'Matched Account',
      render: (l) =>
        l.account ? (
          <Link
            to="/customers/$customerId"
            params={{ customerId: l.account.id }}
            className="text-sm font-medium text-brand-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {l.account.name}
          </Link>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'request',
      header: 'Study Request',
      render: (l) =>
        l.request ? (
          <Link
            to="/study-requests/$requestId"
            params={{ requestId: l.request.id }}
            className="font-mono text-sm font-medium text-brand-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {l.request.referenceNo}
          </Link>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'receivedAt',
      header: 'Received',
      render: (l) => (
        <span className="flex items-center gap-1.5 text-xs text-gray-500">
          <Calendar size={11} className="shrink-0 text-gray-400" />
          {fmt(l.receivedAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (l) => <StatusBadge status={l.processingStatus} />,
    },
    {
      key: 'errorMessage',
      header: 'Error',
      render: (l) =>
        l.errorMessage ? (
          <span
            title={l.errorMessage}
            className="flex max-w-xs items-center gap-1.5 truncate text-xs text-error-600"
          >
            <AlertTriangle size={11} className="shrink-0" />
            {l.errorMessage}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Tahseel Inbound"
        description="Every payload received from Tahseel — click a row to see everything received and, if it never produced a request, create one."
      />
      <DataTable
        columns={columns}
        data={logs}
        keyField="id"
        isLoading={isLoading}
        isError={isError}
        emptyMessage="No inbound requests received yet"
      />
      {selectedLogId && (
        <LogDetailModal
          logId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
          canCreate={canCreate}
          onCreated={handleCreated}
        />
      )}
    </AppLayout>
  );
}
